import React, { useContext } from "react";
import { useRef, useState } from "react";

import { useMutation, useQuery } from "@tanstack/react-query";
import { Button, Col, Divider, Flex, Form, Grid, Input, Popconfirm, Row, Select, Skeleton, notification } from "antd";
import { useForm, useWatch } from "antd/es/form/Form";

import AdicionarAjuda, { ITutorial, ITutorialPassos } from "../../../Components/AdicionarAjuda";
import { CentroCustoService } from "../../../Services/Financeiro/CentroCustoService";
import { CentroCustoTipoService } from "../../../Services/Financeiro/CentroCustoTipoService";
import { PropriedadesDoPopConfirm } from "../../../Defaults";
import { EnumCentroCustoOrigem } from "../../../Models/Financeiro/CentroCustoOrigem";
import { EnumCentroCustoTipo, TypeCentroCustoTipo } from "../../../Models/Financeiro/CentroCustoTipo";
import { EmpresaService } from '../../../Services/Pessoa/EmpresaService';
import { PropriedadesDeNotificacao } from "../../../Utils/Notificacoes";
import { SelectComPesquisa } from "../../../Components/SelectComPesquisa";
import { TypeCentroCusto } from "../../../Models/Financeiro/CentroCusto";
import { TypeEmpresa } from "../../../Models/Pessoa/Empresa";

import { Permissoes } from "../../../Constants/Permissoes";
import { SessaoContext } from "../../../Contexts/SessaoContext";
import { Restrito } from "../../../Components/Restrito";

import { ResponsiveAntdTable } from "../../../Components/ResponsiveAntdTable";
import { Conjunto } from "../../../Components/Conjunto";
import { Ordenar } from "../../../Utils/Ordenacao";

import { Link, matchPath, Route, Routes, useNavigate } from "react-router-dom";
import { useRelativePath } from "../../../Hooks/useNextRelativePath";
import { useRedirection } from "../../../Hooks/useRedirection";
import { useWatchParameterOnRoutes } from "../../../Hooks/useGetParameterOnRoutes";

import * as Tutoriais from "./centroDeCusto.tutorial"

const centroCustoService = new CentroCustoService();
const centroCustoTipoService = new CentroCustoTipoService();
const empresaService = new EmpresaService();

const ListagemDeCentrosDeCustos: React.FC<{
    aoEditar?: (item: TypeCentroCusto) => any,
    aoAdicionar?: () => any

    hrefDoBotaoAdicionar?: string,
    hrefDoBotaoEditar?: (centroDeCusto: TypeCentroCusto) => string | void
}> = (props) => {

    const permissoes = useContext(SessaoContext)?.sessao?.usuario?.permissoes.map((p) => p.nome);
    const tamanhoDaTela = Grid.useBreakpoint();

    const [filtro, setFiltro] = useState<TypeCentroCusto>({});

    const listaCentrosCusto = useQuery({
        queryKey: ['listarCentros', filtro],
        queryFn: () => centroCustoService.listar(filtro),
        gcTime: 0,
        enabled: true
    });

    const listaEmpresas = useQuery({
        queryKey: ['listarEmpresas'],
        queryFn: () => empresaService.listar({ ativo: true }),
    });

    const listaTipo = useQuery({
        queryKey: ['listarTipo'],
        queryFn: () => centroCustoTipoService.listar({})
    });

    interface IFormFiltro {
        ddlEmpresa?: TypeEmpresa["codigo"],
        txtDescricao?: string,
        ddlTipo?: TypeCentroCustoTipo["codigo"],
        ddlAtivo?: boolean
    }

    const filtrarTabela = (values: IFormFiltro) => {
        const centroCustoFiltro: TypeCentroCusto = {
            empresa: {
                codigo: values.ddlEmpresa
            },
            descricao: values.txtDescricao,
            tipo: {
                codigo: values.ddlTipo
            },
            ativo: values.ddlAtivo 
        }
        setFiltro(centroCustoFiltro);
    }

    const adicionar = permissoes?.includes(Permissoes.CriarCentroDeCusto);

    return (
        <>
            <Form name="listar" onFinish={filtrarTabela} layout="vertical" autoComplete="off" id={Tutoriais.IDs.formularioDeFiltro}>
                <Row gutter={16}>
                    <Col xs={24} md={8} id={Tutoriais.IDs.filtroDeEmpresa}>
                        <Form.Item<IFormFiltro> name="ddlEmpresa" label="Empresa">
                            <SelectComPesquisa
                                allowClear
                                placeholder={"Todos"}
                                options={listaEmpresas.data?.data.map((m) => {
                                    return {
                                        value: m.codigo,
                                        label: m.nome!,
                                    };
                                })}
                            />
                        </Form.Item>
                    </Col>

                    <Col xs={24} md={8} id={Tutoriais.IDs.filtroDeDescricao}>
                        <Form.Item<IFormFiltro> name="txtDescricao" label="Descrição">
                            <Input placeholder="Todos" />
                        </Form.Item>
                    </Col>

                    <Col xs={24} md={5} id={Tutoriais.IDs.filtroDeTipo}>
                        <Form.Item<IFormFiltro> name="ddlTipo" label="Tipo">
                            <Select
                                allowClear
                                placeholder={"Todos"}
                                options={listaTipo.data?.data.map((x) => {
                                    return { value: x.codigo, label: x.tipo };
                                })}
                            />
                        </Form.Item>
                    </Col>

                    <Col xs={24} md={3} id={Tutoriais.IDs.filtroDeAtivo}>
                        <Form.Item<IFormFiltro> name="ddlAtivo" label="Ativo" initialValue={true}>
                            <Select
                                allowClear
                                placeholder={"Todos"}
                                options={[
                                    { value: true, label: "Ativo" },
                                    { value: false, label: "Inativo" },
                                ]}
                            />
                        </Form.Item>
                    </Col>

                    <Col xs={adicionar ? 12 : 24} md={2}>
                        <Form.Item>
                            <Button
                                type="primary"
                                id={Tutoriais.IDs.botaoListar}
                                key="submit"
                                htmlType="submit"
                                disabled={listaCentrosCusto.isFetching}
                                style={{ width: "100%" }}
                            >
                                Listar
                            </Button>
                        </Form.Item>
                    </Col>

                    <Restrito aberto={adicionar}>
                        <Col xs={12} md={3}>
                            <Form.Item>
                                <Link
                                    to={props.hrefDoBotaoAdicionar ?? "#"}
                                    onClick={(e) => {
                                        !props.hrefDoBotaoAdicionar && e.preventDefault();
                                    }}
                                    type="button"
                                >
                                    <Button
                                        type="primary"
                                        onClick={() => {
                                            props.aoAdicionar?.();
                                        }}
                                        style={{ width: "100%" }}
                                    >
                                        Adicionar
                                    </Button>
                                </Link>
                            </Form.Item>
                        </Col>
                    </Restrito>
                </Row>
            </Form>

            <Row>
                <Col xs={24}>
                    <ResponsiveAntdTable<TypeCentroCusto>
                        bordered
                        locale={{ emptyText: "Nenhum centro de custo encontrado." }}
                        dataSource={listaCentrosCusto.data?.data}
                        loading={listaCentrosCusto.isFetching}
                        rowKey={(k) => k.codigo!}
                        addTitleOn="lg"
                        columns={[
                            {
                                title: "Código",
                                breakpoints: { xs: 12, sm: 12, lg: 2 },
                                dataIndex: "codigo",
                                sorter: (a, b) => Ordenar.Numero(a.codigo, b.codigo),
                                key: "codigo",
                            },
                            {
                                title: "Ativo",
                                dataIndex: ["ativo"],
                                breakpoints: { xs: 12, sm: 12, lg: 2 },
                                key: "ativo",
                                sorter: (a, b) => Ordenar.Booleano(a.ativo, b.ativo),
                                render: (v: boolean) => {
                                    return <>{v ? "Sim" : "Não"}</>;
                                },
                            },
                            {
                                title: "Tipo",
                                breakpoints: { xs: 12, sm: 12, lg: 2 },
                                dataIndex: ["tipo", "tipo"],
                                sorter: (a, b) => Ordenar.String(a.tipo?.tipo, b.tipo?.tipo),
                                key: "tipo",
                            },
                            {
                                title: "Empresa",
                                breakpoints: { xs: 12, sm: 12, lg: 6 },
                                dataIndex: ["empresa", "nome"],
                                sorter: (a, b) => Ordenar.String(a.empresa?.nome, b.empresa?.nome),
                                key: "empresa",
                            },
                            {
                                title: "Descrição",
                                breakpoints: { xs: 24, sm: 24, lg: 8 },
                                dataIndex: ["descricao"],
                                sorter: (a, b) => Ordenar.String(a.descricao, b.descricao),
                                key: "descricao",
                            },
                            {
                                title: "Ações",
                                key: "acoes",
                                noResponsiveTitle: true,
                                breakpoints: { xs: 24, sm: 24, lg: 4 },
                                hidden: [Permissoes.AtualizarCentroDeCusto, Permissoes.DesativarCentroDeCusto, Permissoes.AtivarCentroDeCusto].every(
                                    (p) => !permissoes?.includes(p)
                                ),
                                render: (_, item) => {
                                    return (
                                        <Conjunto
                                            style={{ justifyContent: tamanhoDaTela.sm ? "unset" : "center", display: "flex", flexWrap: "wrap" }}
                                            itens={(() => {
                                                if (item.ativo) {
                                                    return [
                                                        permissoes?.includes(Permissoes.CriarCentroDeCusto) && (
                                                            <Link
                                                                to={props.hrefDoBotaoEditar?.(item) ?? "#"}
                                                                onClick={(e) => !props.hrefDoBotaoEditar && e.preventDefault()}
                                                            >
                                                                <Button
                                                                    className={Tutoriais.Classes.botaoEditar}
                                                                    type="primary"
                                                                    size="small"
                                                                    onClick={() => {
                                                                        props.aoEditar?.(item);
                                                                    }}
                                                                >
                                                                    Editar
                                                                </Button>
                                                            </Link>
                                                        ),

                                                        permissoes?.includes(Permissoes.DesativarCentroDeCusto) && (
                                                            <Popconfirm
                                                                {...PropriedadesDoPopConfirm("desativar")}
                                                                onConfirm={() => {
                                                                    if (item.codigo == null) return;
                                                                    centroCustoService
                                                                        .desativar(item.codigo)
                                                                        .then(() => {
                                                                            listaCentrosCusto.refetch();
                                                                        })
                                                                        .catch(() => {});
                                                                }}
                                                            >
                                                                <Button type="default" danger size="small" className={Tutoriais.Classes.botaoDesativar}>
                                                                    Desativar
                                                                </Button>
                                                            </Popconfirm>
                                                        ),
                                                    ];
                                                } else {
                                                    return [
                                                        permissoes?.includes(Permissoes.AtivarCentroDeCusto) && (
                                                            <Popconfirm
                                                                {...PropriedadesDoPopConfirm("ativar")}
                                                                onConfirm={() => {
                                                                    if (item.codigo == null) return;
                                                                    centroCustoService
                                                                        .ativar(item.codigo)
                                                                        .then(() => {
                                                                            listaCentrosCusto.refetch();
                                                                        })
                                                                        .catch(() => {});
                                                                }}
                                                            >
                                                                <Button type="dashed" size="small">
                                                                    Reativar
                                                                </Button>
                                                            </Popconfirm>
                                                        ),
                                                    ];
                                                }
                                            })()}
                                        />
                                    );
                                },
                            },
                        ]}
                        csv={{
                            enabled: true,
                            filename: "centro-de-custo.csv",
                        }}
                    />
                </Col>
            </Row>

            <AdicionarAjuda
                tutoriais={
                    [
                        {
                            titulo: "Como consultar centros de custos",
                            tutorial: Tutoriais.ConsultarCentro,
                        },

                        permissoes?.includes(Permissoes.CriarCentroDeCusto) && {
                            titulo: "Como cadastrar um novo centro de custos",
                            tutorial: Tutoriais.AdicionarCentro,
                        },

                        permissoes?.includes(Permissoes.AtualizarCentroDeCusto) && {
                            titulo: "Como editar um centro de custos",
                            tutorial: Tutoriais.EditarCentro,
                        },

                        permissoes?.includes(Permissoes.DesativarCentroDeCusto) && {
                            titulo: "Como desativar um centro de custos",
                            tutorial: Tutoriais.DesativarCentro,
                        },
                    ].filter((v) => v != false) as ITutorial[]
                }
            />
        </>
    );
}

const CadastroDeCentroDeCusto: React.FC<{
    centroCustoEmAlteracao?: TypeCentroCusto,
    aoCancelar?: () => any,
    depoisDeFinalizar?: () => any
}> = (props) => {
    
    const usuario = useContext(SessaoContext)?.sessao?.usuario;

    const listaEmpresas = useQuery({
        queryKey: ['listar'],
        queryFn: () => empresaService.listar({ ativo: true }),
        enabled: true
    });

    const listarTipo = useQuery({
        queryKey: ['listarTipo'],
        queryFn: () => centroCustoTipoService.listar({})
    });

    const listarGrupos = useQuery({
        queryKey: ['listarGrupos'],
        queryFn: () => centroCustoService.listar({
            tipo: {
                codigo: EnumCentroCustoTipo.GRUPO
            },
            ativo: true
        })
    })

    const insereCentroCusto = useMutation({
        mutationFn: (centro: TypeCentroCusto) => { 
            return centroCustoService.inserir(centro)
        } ,
        onSuccess: () => {
            notification.open({
                ...PropriedadesDeNotificacao.Sucesso('Cadastro realizado com sucesso.')
            })
            props.depoisDeFinalizar?.()
        },
    });

    const atualizaCentroCusto = useMutation({
        mutationFn: (centro: TypeCentroCusto) => centroCustoService.atualizar(centro),
        onSuccess: () => { 
            notification.open({
                ...PropriedadesDeNotificacao.Sucesso('Atualização realizada com sucesso.')
            })
            props.depoisDeFinalizar?.()
        },
    });

    function onFinishCentroCusto(values: TypeCentroCusto) {
        const centroCustoInserir: TypeCentroCusto = {
            ...values,
            codigo: props.centroCustoEmAlteracao?.codigo,
            ativo: true,
            origem: {
                codigo: EnumCentroCustoOrigem.TELA_CADASTRAL
            }
        }

        if (centroCustoInserir?.codigo === undefined)
            insereCentroCusto.mutate(centroCustoInserir!);
        else
            atualizaCentroCusto.mutate(centroCustoInserir!);
    }

    const [formCadastro] = useForm();
    const dadosForm = useWatch([], formCadastro);

    const ddlEmpresa = useRef(null)
    const ddlTipo = useRef(null)
    const txtDescricao = useRef(null)
    const ddlGrupo = useRef(null);
    const btnSalvar = useRef(null);

    const tutorialAdicionarGrupo: ITutorialPassos = {
        steps: [
            {
                title: "Passo 1",
                description: "Selecione a empresa.",
                target: () => ddlEmpresa.current,
            },
            {
                title: "Passo 2",
                description: `Em tipo, selecione "Grupo".`,
                target: () => ddlTipo.current,
            },
            {
                title: "Passo 3",
                description: "Dê um nome ao grupo.",
                target: () => txtDescricao.current,
            },
            {
                title: "Passo 4",
                description: `Clique em "Salvar".`,
                target: () => btnSalvar.current,
            }
        ]
    }

    const tutorialAdicionarItem: ITutorialPassos = {
        steps: [
            {
                title: "Passo 1",
                description: "Selecione a empresa.",
                target: () => ddlEmpresa.current,
            },
            {
                title: "Passo 2",
                description: `Em tipo, selecione "Item".`,
                target: () => ddlTipo.current,
            },
            {
                title: "Passo 3",
                description: "Dê um nome ao item.",
                target: () => txtDescricao.current,
            },
            {
                title: "Passo 4",
                description: "Selecione o grupo.",
                target: () => ddlGrupo.current,
            },
            {
                title: "Passo 5",
                description: `Clique em "Salvar".`,
                target: () => btnSalvar.current,
            }
        ]
    }

    const tutorialMenuItems: ITutorial[] = [
        {
            titulo: "Como adicionar um novo grupo",
            tutorial: tutorialAdicionarGrupo
        },
        {
            titulo: "Como adicionar um novo item",
            tutorial: tutorialAdicionarItem
        },
    ]

    return (
        <>
            <Form autoComplete="off" onFinish={onFinishCentroCusto} form={formCadastro} layout="vertical">
                <Row gutter={16}>
                    <Col xs={24} md={8} ref={ddlEmpresa}>
                        <Form.Item<TypeCentroCusto>
                            name={["empresa", "codigo"]}
                            rules={[{ required: true, message: "Por favor, informe uma empresa!" }]}
                            initialValue={props.centroCustoEmAlteracao?.empresa?.codigo}
                            label="Empresa"
                        >
                            <SelectComPesquisa
                                options={listaEmpresas.data?.data.map((m) => {
                                    return {
                                        value: m.codigo,
                                        label: m.nome!,
                                    };
                                })}
                                placeholder="Selecione"
                                allowClear
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={4} ref={ddlTipo}>
                        <Form.Item<TypeCentroCusto>
                            name={["tipo", "codigo"]}
                            rules={[{ required: true, message: "Por favor, informe um tipo!" }]}
                            initialValue={props.centroCustoEmAlteracao?.tipo?.codigo}
                            label="Tipo"
                        >
                            <Select
                                options={listarTipo.data?.data.map((x) => {
                                    return { value: x.codigo, label: x.tipo };
                                })}
                                placeholder="Selecione"
                                allowClear
                            />
                        </Form.Item>
                    </Col>

                    <Col xs={24} md={8} ref={txtDescricao}>
                        <Form.Item<TypeCentroCusto>
                            name="descricao"
                            rules={[{ required: true, message: "Por favor, informe uma descrição!" }]}
                            initialValue={props.centroCustoEmAlteracao?.descricao}
                            label="Descrição"
                        >
                            <Input placeholder="Descrição" />
                        </Form.Item>
                    </Col>

                    {dadosForm?.tipo?.codigo === EnumCentroCustoTipo.ITEM && (
                        <Col xs={24} md={4} ref={ddlGrupo}>
                            <Form.Item<TypeCentroCusto>
                                name={["grupo", "codigo"]}
                                rules={[{ required: dadosForm?.tipo?.codigo === EnumCentroCustoTipo.ITEM, message: "Por favor, informe um grupo!" }]}
                                initialValue={props.centroCustoEmAlteracao?.grupo?.codigo}
                                label="Grupo"
                            >
                                <Select
                                    placeholder="Selecione"
                                    options={listarGrupos.data?.data.map((x) => {
                                        return { value: x.codigo, label: x.descricao };
                                    })}
                                />
                            </Form.Item>
                        </Col>
                    )}
                </Row>

                <Row>
                    <Col span={24}>
                        <Flex align="center" justify="center" gap={16}>
                            {props.aoCancelar && (
                                <Button
                                    danger
                                    type="primary"
                                    key={"cancel"}
                                    onClick={() => {
                                        props.aoCancelar && props.aoCancelar();
                                    }}
                                >
                                    Cancelar
                                </Button>
                            )}
                            <Button
                                type="primary"
                                ref={btnSalvar}
                                key="submit"
                                htmlType="submit"
                                disabled={
                                    !usuario?.permissoes
                                        .map((p) => p.nome)
                                        .includes(
                                            props.centroCustoEmAlteracao != undefined ? Permissoes.AtualizarCentroDeCusto : Permissoes.CriarCentroDeCusto
                                        ) ||
                                    insereCentroCusto.isPending ||
                                    atualizaCentroCusto.isPending
                                }
                            >
                                Salvar
                            </Button>
                        </Flex>
                    </Col>
                </Row>
            </Form>

            <AdicionarAjuda tutoriais={tutorialMenuItems} />
        </>
    );
}

export function CentroCusto() {
    const [centroCustoEmAlteracao, setCentroCustoEmAlteracao] = useState<TypeCentroCusto | undefined>(undefined);
    const permissoes = useContext(SessaoContext)?.sessao?.usuario?.permissoes.map((p) => p.nome);

    const navigate = useNavigate();
    const [raiz, subrota] = useRelativePath();

    const rotaDeEdicao = matchPath({ path: "/editar" }, subrota);
    const rotaDeVisualizacao = matchPath({ path: "/detalhes" }, subrota);

    const obterCentroDeCusto = useMutation({
        mutationFn: (id: number) => new CentroCustoService().listar({ codigo: id, ativo: true }),
        onSuccess: (resposta) => {
            const resultados = resposta.data;
            if (resultados.length == 0) {
                notification.open({
                    ...PropriedadesDeNotificacao.Erro("Nenhum registro foi encontrado para o identificador informado")
                })
                navigate(raiz)
            } else {
                setCentroCustoEmAlteracao(resposta.data.at(0))
            }
        }
    })

    useRedirection([Permissoes.ListarCentrosDeCusto, Permissoes.CriarCentroDeCusto, Permissoes.AtualizarCentroDeCusto, Permissoes.DesativarCentroDeCusto])
    useWatchParameterOnRoutes({ 
        appliablePaths: [rotaDeEdicao, rotaDeVisualizacao], paramToWatch: "id", filters: [(p) => isNaN(Number(p))], 
        onNotAccepted() { navigate(raiz) },
        onExists(param) { obterCentroDeCusto.mutate(Number(param)); }, 
     })

    return ( 
        <>
            <h2 style={{ marginTop: 0 }}> 
                {
                    rotaDeVisualizacao ? "Detalhes do Centro de Custo" : rotaDeEdicao? "Edição do Centro de Custo": "Cadastro de Centro de Custo"
                }
            </h2>

            <Divider />

            <Routes>
                {
                    permissoes?.includes(Permissoes.ListarCentrosDeCusto) &&
                    <Route path="*" index element={
                        <ListagemDeCentrosDeCustos
                            hrefDoBotaoAdicionar={raiz + "/cadastro"}
                            hrefDoBotaoEditar={(item) => {
                                if (item.codigo) {
                                    return raiz + `/editar?id=${item.codigo}`
                                }
                            }}

                            aoEditar={(centroDeCusto) => {
                                setCentroCustoEmAlteracao(centroDeCusto);
                            }}
                        />
                    } />
                }

                {
                    permissoes?.includes(Permissoes.CriarCentroDeCusto) &&
                    <Route path="/cadastro" element={
                        <CadastroDeCentroDeCusto
                            aoCancelar={() => { navigate(raiz) }}
                            depoisDeFinalizar={() => { navigate(raiz) }}
                        />
                    } />
                }

                {
                    permissoes?.includes(Permissoes.AtualizarCentroDeCusto) &&
                    <Route path="/editar" element={
                        centroCustoEmAlteracao == undefined ? <Skeleton active /> :
                            <CadastroDeCentroDeCusto
                                centroCustoEmAlteracao={centroCustoEmAlteracao}
                                aoCancelar={() => { navigate(raiz) }}
                                depoisDeFinalizar={() => { navigate(raiz) }}
                            />
                    } />
                }
            </Routes>

        </>
    );

}